import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { X, AlertTriangle, Copy } from 'lucide-react';
import { useEscapeToClose } from '../../../hooks/useEscapeToClose';
import UserAvatar from './UserAvatar';
import { roleBadgeStyle, roleLabel, formatTz } from './auditConstants';
import type { LoginRow } from './LoginActivityTable';
import { api } from '../../../api/client.ts';

const MONO = "ui-monospace, 'Cascadia Mono', Consolas, monospace";

const RESULT_PILL: Record<LoginRow['result'], { bg: string; fg: string; border: string }> = {
  success: { bg: 'rgba(16,185,129,0.15)', fg: '#10b981', border: 'rgba(16,185,129,0.25)' },
  failed: { bg: 'rgba(239,68,68,0.15)', fg: '#ef4444', border: 'rgba(239,68,68,0.3)' },
  blocked: { bg: 'rgba(239,68,68,0.25)', fg: '#ef4444', border: 'rgba(239,68,68,0.45)' },
  locked: { bg: 'rgba(245,158,11,0.15)', fg: '#f59e0b', border: 'rgba(245,158,11,0.3)' },
};
const RESULT_LABEL: Record<LoginRow['result'], string> = { success: 'Success', failed: 'Failed', blocked: 'Blocked', locked: 'Locked out' };

function DetailSection({ title, rows }: { title: string; rows: { key: string; value: React.ReactNode; mono?: boolean }[] }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase' }}>{title}</div>
      <div style={{ borderRadius: 10, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
        {rows.map((r, i) => (
          <div key={r.key} style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: 10, padding: '9px 12px', borderTop: i === 0 ? 'none' : '1px solid var(--border-subtle)', fontSize: 13 }}>
            <div style={{ color: 'var(--text-muted)' }}>{r.key}</div>
            <div style={{ color: 'var(--text-primary)', wordBreak: 'break-all', fontFamily: r.mono ? MONO : undefined }}>{r.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function LoginDetailPanel({
  row, onClose, onChanged, showToast,
}: {
  row: LoginRow | null;
  onClose: () => void;
  onChanged: () => void;
  showToast: (msg: string, error?: boolean) => void;
}) {
  const isOpen = !!row;
  useEscapeToClose(isOpen, onClose);
  const [confirmBlock, setConfirmBlock] = useState<'block' | 'unblock' | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!row) return null;

  const pill = RESULT_PILL[row.result];
  const isBlocked = !!row.ipBlockedUntil && new Date(row.ipBlockedUntil) > new Date();
  const showRiskCallout = row.isUnusual || row.result === 'blocked' || row.isNewDevice;

  function riskCopy(): string {
    if (row!.isUnusual || row!.result === 'blocked') {
      const parts: string[] = [];
      if (row!.isUnusual && !row!.location.country) parts.push('a script user agent');
      else if (row!.isUnusual) parts.push('first time from this country');
      if (row!.deviceType === 'Script') parts.push('a script user agent');
      const clause = parts.length ? `: ${Array.from(new Set(parts)).join(', ')}` : '';
      if (row!.result === 'blocked') {
        return `Unusual sign-in${clause}. Repeated failed attempts triggered an automatic 24-hour IP block.`;
      }
      return `Unusual sign-in${clause}.`;
    }
    return 'First sign-in from this device for this user. Confirm it was them.';
  }

  async function copyDetails() {
    const text = [
      `Result: ${RESULT_LABEL[row!.result]}${row!.reason ? ` (${row!.reason})` : ''}`,
      `User: ${row!.user?.name ?? 'Unknown'} (${row!.user?.email ?? row!.emailEntered})`,
      `Time: ${formatTz(row!.createdAt, { dateStyle: 'medium', timeStyle: 'medium' })} (Eastern)`,
      `IP: ${row!.ip ?? '—'}`,
      `Location: ${[row!.location.city, row!.location.region, row!.location.country].filter(Boolean).join(', ') || '—'}`,
      `Browser: ${row!.browser ?? '—'}`,
      `OS / device: ${[row!.os, row!.deviceType].filter(Boolean).join(' · ') || '—'}`,
      `Facility: ${row!.facilityName ?? '—'}`,
    ].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      showToast('Copied');
    } catch {
      showToast('Copy failed', true);
    }
  }

  async function doBlockToggle() {
    if (!row!.ip) return;
    setBusy(true);
    try {
      if (isBlocked) {
        await api.delete(`/audit/block-ip/${row!.ip}`);
        showToast('IP unblocked');
      } else {
        await api.post('/audit/block-ip', { ip: row!.ip, hours: 24 });
        showToast('IP blocked for 24 h');
      }
      setConfirmBlock(null);
      onChanged();
      onClose();
    } catch {
      showToast('Action failed', true);
    } finally {
      setBusy(false);
    }
  }

  return ReactDOM.createPortal(
    <>
      <div
        className="audit-overlay-in"
        style={{ position: 'fixed', inset: 0, zIndex: 998, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
        onClick={onClose}
      />
      <div
        className="audit-panel-in"
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 999, width: 460,
          background: 'rgba(20,30,50,0.97)', borderLeft: '1px solid var(--border-default)',
          boxShadow: '-20px 0 50px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{ flexShrink: 0, padding: '20px 24px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', background: '#0d1526' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <UserAvatar userId={row.user?.id ?? null} name={row.user?.name ?? null} size="panel" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text-primary)' }}>{row.user?.name ?? 'Unknown user'}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{row.user?.email ?? `${row.emailEntered} (typed)`}</div>
            </div>
            <button
              onClick={onClose}
              style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <X size={18} />
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
            <span style={{ fontSize: 10, padding: '3px 10px', borderRadius: 99, fontWeight: 600, background: pill.bg, color: pill.fg, border: `1px solid ${pill.border}` }}>
              {RESULT_LABEL[row.result]}
            </span>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              {formatTz(row.createdAt, { month: 'short', day: 'numeric', year: 'numeric' })} at {formatTz(row.createdAt, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })} (Eastern)
            </span>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {showRiskCallout && (
            <div style={{ display: 'flex', gap: 10, padding: '12px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)' }}>
              <AlertTriangle size={16} color="#ef4444" style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={{ fontSize: 13, color: '#fca5a5' }}>{riskCopy()}</div>
            </div>
          )}

          <DetailSection
            title="Attempt"
            rows={[
              { key: 'Result', value: `${RESULT_LABEL[row.result]}${row.reason ? ` — ${row.reason.replace(/_/g, ' ')}` : ''}` },
              { key: 'Method', value: row.method === 'password' ? 'Password' : row.method },
              { key: 'Email entered', value: row.emailEntered },
              { key: 'Session ID', value: row.sessionId ?? '—', mono: true },
              { key: 'Session length', value: '—' },
            ]}
          />

          <DetailSection
            title="Network"
            rows={[
              { key: 'IP address', value: row.ip ?? '—', mono: true },
              { key: 'Location', value: row.ip && !row.location.city && !row.location.country ? 'Internal network' : ([row.location.city, row.location.region, row.location.country].filter(Boolean).join(', ') || '—') },
              { key: 'Provider', value: row.location.org ?? '—' },
              { key: 'Known IP for user', value: row.ipKnownCount > 0 ? `Yes · seen ${row.ipKnownCount} time${row.ipKnownCount === 1 ? '' : 's'}` : 'No · first time' },
              { key: 'IP status', value: isBlocked ? `Blocked until ${formatTz(row.ipBlockedUntil, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })}` : 'Allowed' },
            ]}
          />

          <DetailSection
            title="Device"
            rows={[
              { key: 'Browser', value: row.browser ?? '—' },
              { key: 'OS / device', value: [row.os, row.deviceType].filter(Boolean).join(' · ') || '—' },
              { key: 'User agent', value: row.userAgent ?? '—', mono: true },
              { key: 'Facility', value: row.facilityName ?? '—' },
            ]}
          />
        </div>

        <div style={{ flexShrink: 0, padding: '12px 24px', borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={copyDetails}
            style={{ height: 36, padding: '0 14px', borderRadius: 8, fontSize: '0.84rem', border: '1px solid rgba(255,255,255,0.15)', background: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}
          >
            <Copy size={14} /> Copy details
          </button>
          {row.ip && (
            <button
              onClick={() => setConfirmBlock(isBlocked ? 'unblock' : 'block')}
              style={{ height: 36, padding: '0 14px', borderRadius: 8, fontSize: '0.84rem', fontWeight: 500, border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.12)', color: '#ef4444', cursor: 'pointer' }}
            >
              {isBlocked ? 'Unblock IP' : 'Block IP'}
            </button>
          )}
        </div>
      </div>

      {confirmBlock && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => setConfirmBlock(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 380, width: '90vw' }}>
            <div className="modal-header">
              <h3 className="modal-title">{confirmBlock === 'block' ? 'Block this IP?' : 'Unblock this IP?'}</h3>
              <button className="modal-close" onClick={() => setConfirmBlock(null)}><X size={18} /></button>
            </div>
            <div>
              <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>
                {confirmBlock === 'block'
                  ? <>Block <strong style={{ color: 'var(--text-primary)' }}>{row.ip}</strong> from signing in for 24 hours?</>
                  : <>Allow <strong style={{ color: 'var(--text-primary)' }}>{row.ip}</strong> to sign in again immediately?</>}
              </p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="btn btn-ghost" onClick={() => setConfirmBlock(null)} disabled={busy}>Cancel</button>
                <button className="btn" style={{ background: '#ef4444', color: '#fff' }} onClick={doBlockToggle} disabled={busy}>
                  {confirmBlock === 'block' ? 'Block IP' : 'Unblock IP'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>,
    document.body
  );
}
